'use client';

/**
 * 미디어 카드 상세 다이얼로그 — 어느 카드를 열었나 · 이전/다음 카드로 넘기기.
 *
 * 열린 카드는 key 로 쥐고 지금 그리드 목록에서 찾는다 — 이어 불러오기로 목록이 늘어도 자리가 흔들리지 않는다.
 * 목록에 없는 카드(비슷한 소재 줄에서 넘어온 것)는 그 카드를 통째로 쥔다 — 그때는 이전/다음이 없다.
 * ⚠️ 열린 카드는 주소에 남기지 않는다 — 새로고침하면 그 카드가 목록에 없을 수 있다.
 */

import type { VideoUrlRequestItem } from 'src/types/search-all/video-url';

import { useMemo, useState, useCallback } from 'react';

import type { MediaCard } from '../../utils/search-all-media-cards';

/**
 * @param cards 지금 그리드가 편 카드(순서 그대로)
 * @param onNeedVideo 영상 주소를 물을 거리가 있는 카드를 열면 부른다 — 카드에서 머무는 시간을 못 채우고 열었어도 다이얼로그가 영상을 받게
 */
export function useSearchAllMediaDetail(cards: MediaCard[], onNeedVideo: (item: VideoUrlRequestItem) => void) {
  const [openedKey, setOpenedKey] = useState<string | null>(null);
  /** 목록 밖에서 연 카드 — 비슷한 소재 줄에서 넘어왔다 */
  const [guest, setGuest] = useState<MediaCard | null>(null);

  const index = useMemo(() => (openedKey ? cards.findIndex((c) => c.key === openedKey) : -1), [cards, openedKey]);
  const card = guest ?? (index >= 0 ? cards[index] : null);

  const show = useCallback((next: MediaCard) => {
    const inList = cards.some((c) => c.key === next.key);
    setGuest(inList ? null : next);
    setOpenedKey(inList ? next.key : null);
    if (next.videoAsk && !next.videoSrc) onNeedVideo(next.videoAsk);
  }, [cards, onNeedVideo]);

  const close = useCallback(() => {
    setOpenedKey(null);
    setGuest(null);
  }, []);
  const prev = useCallback(() => {
    if (index > 0) show(cards[index - 1]);
  }, [cards, index, show]);
  const next = useCallback(() => {
    if (index >= 0 && index < cards.length - 1) show(cards[index + 1]);
  }, [cards, index, show]);

  return { card, hasPrev: !guest && index > 0, hasNext: !guest && index >= 0 && index < cards.length - 1, open: show, close, prev, next };
}
